"use client";

import { SettingsRow, SettingsSection } from "@commerce/components/page";
import { useRowKeys } from "@commerce/components/use-row-keys";
import { parsePoint } from "@commerce/orders/meta";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@ui/components/collapsible";
import { Input } from "@ui/components/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@ui/components/input-group";
import { Switch } from "@ui/components/switch";
import { Textarea } from "@ui/components/textarea";
import { cn } from "@ui/lib";
import {
	ArrowLeftIcon,
	CircleAlertIcon,
	MapPinIcon,
	PencilIcon,
	PlusIcon,
	XIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Branch, BranchUpdate } from "./BranchesScreen";
import type { MapTool } from "./BranchMap";
import {
	areaLabel,
	asZone,
	distanceLabel,
	type PolygonGeometry,
	ringFromText,
	ringToText,
	zoneAreaM2,
} from "./geo";
import {
	completeHours,
	DAY_LABEL,
	DAYS,
	type Day,
	defaultHours,
	hoursProblem,
	MAX_RANGES,
	type StoreHours,
	type TimeRange,
} from "./hours";

type Fulfilment = NonNullable<Branch["fulfilment"]>;

interface DeliveryDefaults {
	fee: number;
	freeAbove?: number;
	minimumOrder?: number;
	slotMinutes: number;
	leadMinutes: number;
	capacityPerSlot?: number;
}

/** Rows inside the side pane stack their control under the label at every width. */
const STACKED = "sm:grid-cols-1 sm:gap-3 [&>div:last-child]:sm:items-stretch";
/** A switch or a short control stays beside its label. */
const INLINE =
	"grid-cols-[minmax(0,1fr)_auto] items-center gap-4 sm:grid-cols-[minmax(0,1fr)_auto] [&>div:last-child]:items-end";

/** What a shopper would hit at this branch as it is now, drafts included. */
export function branchProblems(branch: Branch): string[] {
	const problems: string[] = [];
	if (
		branch.deliveryEnabled &&
		!branch.deliveryZone &&
		branch.deliveryRadiusM === null
	) {
		problems.push(
			"No delivery area: no address is matched to this branch.",
		);
	}
	if (
		branch.deliveryRadiusM !== null &&
		!branch.deliveryZone &&
		(branch.latitude === null || branch.longitude === null)
	) {
		problems.push("A radius needs the branch's location.");
	}
	if (!branch.hours) {
		problems.push(
			"No opening hours: shoppers get one as-soon-as-possible slot.",
		);
	}
	if (!branch.deliveryEnabled && !branch.pickupEnabled) {
		problems.push(
			"Neither delivery nor pickup is on: no order can reach it.",
		);
	}
	return problems;
}

/** The selected branch: where it is, where it delivers, its hours and its delivery terms. */
export function BranchDetail({
	branch,
	saved,
	isAdmin,
	overlapsWith,
	defaults,
	onChange,
	onDiscard,
	onBack,
	onTool,
}: {
	branch: Branch;
	saved: Branch;
	isAdmin: boolean;
	overlapsWith: string[];
	defaults: DeliveryDefaults | null;
	onChange: (change: BranchUpdate) => void;
	onDiscard: () => void;
	onBack: () => void;
	onTool: (tool: MapTool) => void;
}) {
	const problems = branchProblems(branch);
	const changed = JSON.stringify(branch) !== JSON.stringify(saved);
	const today = new Date().toISOString().slice(0, 10);
	const hoursError = branch.hours ? hoursProblem(branch.hours, today) : null;
	return (
		<div className="flex flex-col gap-4 p-4">
			<div className="flex items-start gap-2">
				<Button
					variant="ghost"
					size="icon-sm"
					className="-ml-1 lg:hidden"
					aria-label="All branches"
					onClick={onBack}
				>
					<ArrowLeftIcon />
				</Button>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<h2 className="flex flex-wrap items-center gap-2 font-semibold text-lg tracking-tight">
						<span dir="auto" className="truncate">
							{branch.name}
						</span>
						{branch.isDefault ? (
							<Badge variant="secondary">Default</Badge>
						) : null}
					</h2>
					<p className="text-muted-foreground text-xs">
						Branch id {branch.externalId} · {branch.currency}
						{branch.geoSource === "connector"
							? " · location from your connector"
							: branch.geoSource === "admin"
								? " · area set here"
								: ""}
					</p>
				</div>
				{changed && isAdmin ? (
					<Button variant="ghost" size="sm" onClick={onDiscard}>
						Undo changes
					</Button>
				) : null}
			</div>

			{!isAdmin ? (
				<p className="rounded-2xl bg-muted/60 px-3 py-2 text-muted-foreground text-sm">
					Only admins of this retailer can change branches.
				</p>
			) : null}

			{problems.length > 0 || overlapsWith.length > 0 ? (
				<ul className="flex flex-col gap-1.5 rounded-2xl bg-warning/12 px-3 py-2.5 text-sm dark:bg-warning/10">
					{problems.map((problem) => (
						<li key={problem} className="flex gap-2">
							<CircleAlertIcon
								className="mt-0.5 size-4 shrink-0 text-warning-foreground dark:text-warning"
								aria-hidden
							/>
							{problem}
						</li>
					))}
					{overlapsWith.length > 0 ? (
						<li className="flex gap-2">
							<CircleAlertIcon
								className="mt-0.5 size-4 shrink-0 text-warning-foreground dark:text-warning"
								aria-hidden
							/>
							<span>
								Overlaps {overlapsWith.join(", ")}. An address
								in both goes to the branch with the higher
								priority.
							</span>
						</li>
					) : null}
				</ul>
			) : null}

			<DeliveryArea
				branch={branch}
				isAdmin={isAdmin}
				onChange={onChange}
				onTool={onTool}
			/>
			<Location
				branch={branch}
				isAdmin={isAdmin}
				onChange={onChange}
				onTool={onTool}
			/>

			<SettingsSection title="Orders">
				<SettingsRow
					className={INLINE}
					label="Delivery"
					description="Addresses inside the area are delivered from here."
				>
					<Switch
						checked={branch.deliveryEnabled}
						disabled={!isAdmin}
						onCheckedChange={(deliveryEnabled) =>
							onChange({ deliveryEnabled })
						}
						aria-label="Delivery"
					/>
				</SettingsRow>
				<SettingsRow
					className={INLINE}
					label="Pickup in store"
					description="Shoppers can collect their order here."
				>
					<Switch
						checked={branch.pickupEnabled}
						disabled={!isAdmin}
						onCheckedChange={(pickupEnabled) =>
							onChange({ pickupEnabled })
						}
						aria-label="Pickup in store"
					/>
				</SettingsRow>
				<SettingsRow
					className={INLINE}
					label="Priority"
					info="When two areas overlap, an address there goes to the branch with the higher number."
				>
					<Input
						type="number"
						inputMode="numeric"
						className="w-20 tabular"
						value={branch.priority}
						disabled={!isAdmin}
						aria-label="Priority"
						onChange={(e) =>
							onChange({
								priority: Math.round(
									Number(e.target.value) || 0,
								),
							})
						}
					/>
				</SettingsRow>
			</SettingsSection>

			<HoursSection
				hours={branch.hours}
				error={hoursError}
				today={today}
				isAdmin={isAdmin}
				onChange={(hours) => onChange({ hours })}
			/>

			<TermsSection
				fulfilment={branch.fulfilment}
				currency={branch.currency}
				defaults={defaults}
				isAdmin={isAdmin}
				onChange={(fulfilment) => onChange({ fulfilment })}
			/>
		</div>
	);
}

function DeliveryArea({
	branch,
	isAdmin,
	onChange,
	onTool,
}: {
	branch: Branch;
	isAdmin: boolean;
	onChange: (change: BranchUpdate) => void;
	onTool: (tool: MapTool) => void;
}) {
	const zone = asZone(branch.deliveryZone);
	return (
		<SettingsSection
			title="Delivery area"
			info="A drawn zone wins over a radius. Draw on the map, or type the radius or the zone's corners here."
		>
			<SettingsRow className={STACKED} label="Area">
				<p className="text-sm">{areaSummary(branch)}</p>
				{isAdmin ? (
					<div className="flex flex-wrap gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => onTool("zone")}
						>
							{zone ? "Draw again" : "Draw a zone"}
						</Button>
						{zone?.type === "Polygon" ? (
							<Button
								variant="outline"
								size="sm"
								onClick={() => onTool("edit")}
							>
								<PencilIcon data-icon="inline-start" />
								Edit on the map
							</Button>
						) : null}
					</div>
				) : null}
			</SettingsRow>
			{!zone ? (
				<RadiusRow
					radiusM={branch.deliveryRadiusM}
					isAdmin={isAdmin}
					onChange={(deliveryRadiusM) =>
						onChange({ deliveryRadiusM })
					}
				/>
			) : null}
			{isAdmin ? (
				<ZoneCorners
					initial={zone ? ringToText(zone) : ""}
					onZone={(deliveryZone) =>
						onChange({ deliveryZone, deliveryRadiusM: null })
					}
				/>
			) : null}
		</SettingsSection>
	);
}

/** "Zone drawn · 4.2 km²", "3 km around the branch" or "None yet". */
function areaSummary(branch: Branch): string {
	const zone = asZone(branch.deliveryZone);
	if (zone) {
		return `Zone drawn · ${areaLabel(zoneAreaM2(zone))}`;
	}
	return branch.deliveryRadiusM
		? `${distanceLabel(branch.deliveryRadiusM)} around the branch`
		: "None yet";
}

function RadiusRow({
	radiusM,
	isAdmin,
	onChange,
}: {
	radiusM: number | null;
	isAdmin: boolean;
	onChange: (radiusM: number | null) => void;
}) {
	const radiusId = useId();
	return (
		<SettingsRow
			className={STACKED}
			label="Radius"
			htmlFor={radiusId}
			description="Used when no zone is drawn, measured from the branch's location."
		>
			<InputGroup>
				<InputGroupInput
					id={radiusId}
					type="number"
					inputMode="decimal"
					min={0.1}
					max={100}
					step={0.1}
					value={radiusM !== null ? radiusM / 1000 : ""}
					disabled={!isAdmin}
					placeholder="No radius"
					className="tabular"
					onChange={(e) =>
						onChange(
							e.target.value === ""
								? null
								: Math.min(
										100_000,
										Math.max(
											100,
											Math.round(
												Number(e.target.value) * 1000,
											),
										),
									),
						)
					}
				/>
				<InputGroupAddon align="inline-end">km</InputGroupAddon>
			</InputGroup>
		</SettingsRow>
	);
}

/** The zone's corners typed as text, for an area that is easier to type than to draw. */
function ZoneCorners({
	initial,
	onZone,
}: {
	initial: string;
	onZone: (zone: PolygonGeometry) => void;
}) {
	const [open, setOpen] = useState(false);
	const [text, setText] = useState(initial);
	const [error, setError] = useState<string | null>(null);
	const pointsId = useId();
	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<div className="py-3">
				<CollapsibleTrigger
					render={
						<Button variant="ghost" size="sm" className="-ml-2.5" />
					}
				>
					{open
						? "Hide the zone's corners"
						: "Type the zone's corners"}
				</CollapsibleTrigger>
				<CollapsibleContent className="flex flex-col gap-2 pt-2">
					<label
						htmlFor={pointsId}
						className="text-muted-foreground text-sm"
					>
						One corner per line, as latitude, longitude.
					</label>
					<Textarea
						id={pointsId}
						rows={5}
						className="font-mono text-xs"
						value={text}
						placeholder={
							"24.4539, 54.3773\n24.4602, 54.3901\n24.4471, 54.3958"
						}
						onChange={(e) => {
							setText(e.target.value);
							setError(null);
						}}
						aria-invalid={error ? true : undefined}
					/>
					{error ? (
						<p className="text-destructive text-sm">{error}</p>
					) : null}
					<Button
						variant="outline"
						size="sm"
						className="self-start"
						onClick={() => {
							const parsed = ringFromText(text);
							if ("error" in parsed) {
								setError(parsed.error);
								return;
							}
							onZone(parsed.zone);
						}}
					>
						Use these corners
					</Button>
				</CollapsibleContent>
			</div>
		</Collapsible>
	);
}

function Location({
	branch,
	isAdmin,
	onChange,
	onTool,
}: {
	branch: Branch;
	isAdmin: boolean;
	onChange: (change: BranchUpdate) => void;
	onTool: (tool: MapTool) => void;
}) {
	const coordinates =
		branch.latitude !== null && branch.longitude !== null
			? `${branch.latitude.toFixed(6)}, ${branch.longitude.toFixed(6)}`
			: "";
	const [text, setText] = useState(coordinates);
	const [error, setError] = useState<string | null>(null);
	const [seen, setSeen] = useState(coordinates);
	// A pin dropped on the map replaces what was typed.
	if (seen !== coordinates) {
		setSeen(coordinates);
		setText(coordinates);
		setError(null);
	}
	const pointId = useId();
	const addressId = useId();
	const phoneId = useId();
	return (
		<SettingsSection title="Location">
			<SettingsRow
				className={STACKED}
				label="Where the branch is"
				htmlFor={pointId}
				description="Paste a Google Maps link or coordinates, or pick it on the map."
			>
				<div className="flex w-full gap-2">
					<Input
						id={pointId}
						value={text}
						disabled={!isAdmin}
						placeholder="24.4539, 54.3773"
						className="tabular"
						aria-invalid={error ? true : undefined}
						onChange={(e) => {
							setText(e.target.value);
							setError(null);
						}}
						onBlur={() => {
							if (text.trim() === coordinates) {
								return;
							}
							if (!text.trim()) {
								onChange({ latitude: null, longitude: null });
								return;
							}
							const point = parsePoint(text);
							if (!point) {
								setError("Couldn't read a location from that");
								return;
							}
							onChange(point);
						}}
					/>
					{isAdmin ? (
						<Button variant="outline" onClick={() => onTool("pin")}>
							<MapPinIcon data-icon="inline-start" />
							Pick
						</Button>
					) : null}
				</div>
				{error ? (
					<p className="text-destructive text-sm">{error}</p>
				) : null}
			</SettingsRow>
			<SettingsRow
				className={STACKED}
				label="Address"
				htmlFor={addressId}
			>
				<Input
					id={addressId}
					value={branch.address ?? ""}
					disabled={!isAdmin}
					dir="auto"
					maxLength={300}
					placeholder="Street, area, city"
					onChange={(e) =>
						onChange({ address: e.target.value || null })
					}
				/>
			</SettingsRow>
			<SettingsRow
				className={STACKED}
				label="Phone"
				htmlFor={phoneId}
				description="Given to shoppers who pick up here."
			>
				<Input
					id={phoneId}
					type="tel"
					value={branch.phone ?? ""}
					disabled={!isAdmin}
					maxLength={30}
					placeholder="+971 2 555 0100"
					onChange={(e) =>
						onChange({ phone: e.target.value || null })
					}
				/>
			</SettingsRow>
		</SettingsSection>
	);
}

/** "00:00" as a closing time means midnight, stored as "24:00". */
const toInput = (time: string) => (time === "24:00" ? "00:00" : time);
const fromInput = (time: string, end: boolean) =>
	end && time === "00:00" ? "24:00" : time;

function HoursSection({
	hours,
	error,
	today,
	isAdmin,
	onChange,
}: {
	hours: StoreHours | null;
	error: string | null;
	today: string;
	isAdmin: boolean;
	onChange: (hours: Branch["hours"]) => void;
}) {
	const [closedDate, setClosedDate] = useState("");
	if (!hours) {
		return (
			<SettingsSection title="Opening hours">
				<SettingsRow
					className={STACKED}
					label="No hours set"
					description="Shoppers are offered one as-soon-as-possible slot at any time."
				>
					{isAdmin ? (
						<Button
							variant="outline"
							size="sm"
							onClick={() => onChange(defaultHours())}
						>
							Set opening hours
						</Button>
					) : null}
				</SettingsRow>
			</SettingsSection>
		);
	}
	const setDay = (day: Day, ranges: TimeRange[]) =>
		onChange(
			completeHours({
				...hours,
				weekly: { ...hours.weekly, [day]: ranges },
			}),
		);
	return (
		<SettingsSection
			title="Opening hours"
			footer={
				isAdmin ? (
					<>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onChange(null)}
						>
							Remove hours
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								onChange(
									completeHours({
										...hours,
										weekly: Object.fromEntries(
											DAYS.map((day) => [
												day,
												hours.weekly.mon ?? [],
											]),
										),
									}),
								)
							}
						>
							Copy Monday to every day
						</Button>
					</>
				) : null
			}
		>
			<div className="flex flex-col gap-2 py-4">
				{DAYS.map((day) => (
					<DayHours
						key={day}
						day={day}
						ranges={hours.weekly[day] ?? []}
						isAdmin={isAdmin}
						onChange={(ranges) => setDay(day, ranges)}
					/>
				))}
				{error ? (
					<p className="flex items-center gap-2 text-destructive text-sm">
						<CircleAlertIcon className="size-4" aria-hidden />
						{error}
					</p>
				) : null}
			</div>
			<SettingsRow
				className={STACKED}
				label="Closed on"
				description="Holidays and other days the branch takes no orders."
			>
				{hours.closed.length > 0 ? (
					<ul className="flex flex-wrap gap-1.5">
						{[...hours.closed].sort().map((date) => (
							<li key={date}>
								<Badge
									variant="outline"
									className={cn(
										"tabular",
										date < today && "text-muted-foreground",
									)}
								>
									{date}
									{isAdmin ? (
										<button
											type="button"
											className="-mr-1 rounded-full outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
											aria-label={`Remove ${date}`}
											onClick={() =>
												onChange(
													completeHours({
														...hours,
														closed: hours.closed.filter(
															(d) => d !== date,
														),
													}),
												)
											}
										>
											<XIcon className="size-3" />
										</button>
									) : null}
								</Badge>
							</li>
						))}
					</ul>
				) : null}
				{isAdmin ? (
					<form
						className="flex gap-2"
						onSubmit={(event) => {
							event.preventDefault();
							if (
								!closedDate ||
								closedDate < today ||
								hours.closed.includes(closedDate)
							) {
								return;
							}
							onChange(
								completeHours({
									...hours,
									closed: [...hours.closed, closedDate],
								}),
							);
							setClosedDate("");
						}}
					>
						<Input
							type="date"
							min={today}
							value={closedDate}
							className="h-8 w-40 tabular"
							aria-label="A day the branch is closed"
							onChange={(e) => setClosedDate(e.target.value)}
						/>
						<Button
							type="submit"
							variant="outline"
							size="sm"
							disabled={!closedDate || closedDate < today}
						>
							Add day
						</Button>
					</form>
				) : null}
			</SettingsRow>
		</SettingsSection>
	);
}

function TermsSection({
	fulfilment,
	currency,
	defaults,
	isAdmin,
	onChange,
}: {
	fulfilment: Branch["fulfilment"];
	currency: string;
	defaults: DeliveryDefaults | null;
	isAdmin: boolean;
	onChange: (fulfilment: Branch["fulfilment"]) => void;
}) {
	const set = (patch: Partial<Fulfilment>) => {
		const next: Fulfilment = { ...fulfilment, ...patch };
		for (const key of Object.keys(next) as (keyof Fulfilment)[]) {
			if (next[key] === undefined) {
				delete next[key];
			}
		}
		onChange(Object.keys(next).length > 0 ? next : null);
	};
	const fallback = (value: number | undefined, unit = "") =>
		value === undefined ? "Default: none" : `Default: ${value}${unit}`;
	const moneyField = (
		key: "fee" | "freeAbove" | "minimumOrder",
		label: string,
		defaultValue: number | undefined,
	) => (
		<TermField
			label={label}
			unit={currency}
			value={fulfilment?.[key] ?? ""}
			placeholder={defaults ? fallback(defaultValue) : "Ordering default"}
			disabled={!isAdmin}
			decimal
			onChange={(value) =>
				set({ [key]: value === "" ? undefined : value })
			}
		/>
	);
	const minutesField = (
		key: "slotMinutes" | "leadMinutes" | "capacityPerSlot",
		label: string,
		unit: string,
		defaultValue: number | undefined,
		min: number,
	) => (
		<TermField
			label={label}
			unit={unit}
			value={
				fulfilment?.[key] !== undefined ? String(fulfilment[key]) : ""
			}
			placeholder={defaults ? fallback(defaultValue) : "Ordering default"}
			disabled={!isAdmin}
			onChange={(value) =>
				set({
					[key]:
						value === ""
							? undefined
							: Math.max(min, Math.round(Number(value))),
				})
			}
		/>
	);
	return (
		<SettingsSection
			title="Delivery terms"
			info="Empty fields use the defaults from Ordering setup."
		>
			<div className="grid grid-cols-1 gap-3 py-4 min-[420px]:grid-cols-2">
				{moneyField("fee", "Delivery fee", defaults?.fee)}
				{moneyField(
					"minimumOrder",
					"Minimum order",
					defaults?.minimumOrder,
				)}
				{moneyField(
					"freeAbove",
					"Free delivery above",
					defaults?.freeAbove,
				)}
				{minutesField(
					"slotMinutes",
					"Slot length",
					"min",
					defaults?.slotMinutes,
					15,
				)}
				{minutesField(
					"leadMinutes",
					"Ready in at least",
					"min",
					defaults?.leadMinutes,
					0,
				)}
				{minutesField(
					"capacityPerSlot",
					"Orders per slot",
					"orders",
					defaults?.capacityPerSlot,
					1,
				)}
			</div>
		</SettingsSection>
	);
}

function TermField({
	label,
	unit,
	value,
	placeholder,
	disabled,
	decimal,
	onChange,
}: {
	label: string;
	unit: string;
	value: string;
	placeholder: string;
	disabled: boolean;
	decimal?: boolean;
	onChange: (value: string) => void;
}) {
	const id = useId();
	return (
		<div className="flex flex-col gap-1">
			<label htmlFor={id} className="text-muted-foreground text-xs">
				{label}
			</label>
			<InputGroup>
				<InputGroupInput
					id={id}
					type="number"
					inputMode={decimal ? "decimal" : "numeric"}
					min={0}
					step={decimal ? "0.01" : "1"}
					value={value}
					placeholder={placeholder}
					disabled={disabled}
					className="tabular placeholder:text-xs"
					onChange={(e) =>
						onChange(
							e.target.value === ""
								? ""
								: String(Math.max(0, Number(e.target.value))),
						)
					}
				/>
				<InputGroupAddon align="inline-end">{unit}</InputGroupAddon>
			</InputGroup>
		</div>
	);
}

/** One day's opening hours: closed, or up to MAX_RANGES time ranges. */
function DayHours({
	day,
	ranges,
	isAdmin,
	onChange,
}: {
	day: Day;
	ranges: TimeRange[];
	isAdmin: boolean;
	onChange: (ranges: TimeRange[]) => void;
}) {
	// Ranges carry no id and their times change as they are typed: keys of their own keep
	// each time input mounted (and focused) while it is edited.
	const rows = useRowKeys(ranges.length);
	const open = ranges.length > 0;
	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
			<span className="flex w-28 shrink-0 items-center gap-2 text-sm">
				<Switch
					size="sm"
					checked={open}
					disabled={!isAdmin}
					aria-label={`Open on ${DAY_LABEL[day]}`}
					onCheckedChange={(next) =>
						onChange(next ? [["08:00", "23:00"]] : [])
					}
				/>
				{DAY_LABEL[day].slice(0, 3)}
			</span>
			{open ? (
				<div className="flex min-w-0 flex-1 flex-col gap-1.5">
					{ranges.map(([start, end], index) => (
						<div
							key={rows.keys[index]}
							className="flex items-center gap-1.5"
						>
							<Input
								type="time"
								value={toInput(start)}
								disabled={!isAdmin}
								className="h-8 w-[6.5rem] tabular"
								aria-label={`${DAY_LABEL[day]} opens`}
								onChange={(e) =>
									onChange(
										ranges.map((r, i) =>
											i === index
												? [
														fromInput(
															e.target.value,
															false,
														),
														r[1],
													]
												: r,
										),
									)
								}
							/>
							<span className="text-muted-foreground text-sm">
								to
							</span>
							<Input
								type="time"
								value={toInput(end)}
								disabled={!isAdmin}
								className="h-8 w-[6.5rem] tabular"
								aria-label={`${DAY_LABEL[day]} closes`}
								onChange={(e) =>
									onChange(
										ranges.map((r, i) =>
											i === index
												? [
														r[0],
														fromInput(
															e.target.value,
															true,
														),
													]
												: r,
										),
									)
								}
							/>
							{isAdmin && index > 0 ? (
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label={`Remove the second ${DAY_LABEL[day]} range`}
									onClick={() => {
										rows.removed(index);
										onChange(
											ranges.filter(
												(_, i) => i !== index,
											),
										);
									}}
								>
									<XIcon />
								</Button>
							) : null}
							{isAdmin &&
							index === 0 &&
							ranges.length < MAX_RANGES ? (
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label={`Add a second range on ${DAY_LABEL[day]}`}
									onClick={() => {
										rows.added();
										onChange([
											...ranges,
											["16:00", "23:00"],
										]);
									}}
								>
									<PlusIcon />
								</Button>
							) : null}
						</div>
					))}
				</div>
			) : (
				<span className="text-muted-foreground text-sm">Closed</span>
			)}
		</div>
	);
}
